import { queryOptions } from '@tanstack/react-query';

import { queryClient } from '@/services/query-client';

import { fetchCarrierServices } from '../data-provider';
import type { CarrierService, CarrierServiceFilters, CarrierServices } from '../models';
import { CACHE_KEYS } from '../types';
import { logger } from '../utils/logger';

/**
 * Internal carrier service lookup cache used to resolve carrier service references
 * without repeatedly requesting the same carrier service data.
 *
 * Backed by React Query today, but can be migrated to another storage
 * implementation (e.g. IndexedDB) without changing consumers.
 */

export const carrierServicesQueryOptions = (
  filters: Partial<CarrierServiceFilters> = {},
  { enabled = true }: { enabled?: boolean } = {},
) =>
  queryOptions({
    enabled,
    queryFn: async ({ signal }) => {
      const carrierServices = await fetchCarrierServices(filters, { signal });
      put(carrierServices.list);
      return carrierServices;
    },
    queryKey: CACHE_KEYS.carrierServices(filters),
    // biome-ignore lint/style/noMagicNumbers: 5 minutes
    staleTime: 1000 * 60 * 5,
  });

const INTERNAL_CARRIER_SERVICES_CACHE_KEY = btoa('internal-carrier-services-cache-key');

const INTERNAL_QUERY_CARRIER_SERVICES_KEY = carrierServicesQueryOptions({
  description: INTERNAL_CARRIER_SERVICES_CACHE_KEY,
}).queryKey;

const getCarrierServiceFallback = (carrierServiceId: string): CarrierService => {
  logger.warn(`Carrier service "${carrierServiceId}" was not found. Verify the data model.`);
  return {
    description: carrierServiceId,
    id: carrierServiceId,
    label: carrierServiceId,
    serviceCode: carrierServiceId,
    serviceKey: Number(carrierServiceId) || 0,
  };
};

export const put = async (carrierServices: CarrierService[]) => {
  // Sync with storage (currently React Query; may use IndexedDB in the future).
  const data = queryClient.getQueryData(INTERNAL_QUERY_CARRIER_SERVICES_KEY);

  const newCarrierServices = carrierServices.reduce(
    (acc, carrierService) => {
      acc[String(carrierService.id)] = carrierService;
      return acc;
    },
    { ...(data?.map ?? {}) },
  );

  queryClient.setQueryData(INTERNAL_QUERY_CARRIER_SERVICES_KEY, {
    list: Object.values(newCarrierServices),
    map: newCarrierServices,
  });
};

export const getAll = (): CarrierServices | undefined => {
  return queryClient.getQueryData(INTERNAL_QUERY_CARRIER_SERVICES_KEY);
};

export const find = async (carrierServiceId: string): Promise<CarrierService> => {
  const data = queryClient.getQueryData(INTERNAL_QUERY_CARRIER_SERVICES_KEY);

  const carrierService = data?.map[carrierServiceId];
  if (carrierService) {
    return carrierService;
  }

  try {
    const newData = await queryClient.query(carrierServicesQueryOptions({ search: { serviceKey: carrierServiceId } }));
    if (newData.map[carrierServiceId]) {
      void put(newData.list);
      return newData.map[carrierServiceId];
    }
  } catch {
    return getCarrierServiceFallback(carrierServiceId);
  }

  return getCarrierServiceFallback(carrierServiceId);
};

export const findAll = async (carrierServiceIds: string[]): Promise<CarrierService[]> => {
  const data = queryClient.getQueryData(INTERNAL_QUERY_CARRIER_SERVICES_KEY);

  const missingIds: string[] = [];
  const carrierServices: CarrierService[] = [];

  for (const carrierServiceId of carrierServiceIds) {
    const s = data?.map[carrierServiceId];
    if (s) {
      carrierServices.push(s);
    } else {
      missingIds.push(carrierServiceId);
    }
  }

  if (!missingIds.length) {
    return carrierServices;
  }

  try {
    const newData = await queryClient.query(
      carrierServicesQueryOptions({ search: { serviceKey: missingIds.join(',') } }),
    );
    void put(newData.list);
    for (const missingId of missingIds) {
      const s = newData.map[missingId];
      if (s) {
        carrierServices.push(s);
      } else {
        carrierServices.push(getCarrierServiceFallback(missingId));
      }
    }
  } catch {
    for (const missingId of missingIds) {
      carrierServices.push(getCarrierServiceFallback(missingId));
    }
  }

  return carrierServices;
};
